import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import PublicLayout from '@/components/hydlib/PublicLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";
const labelStyle = { display: 'block', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '11px 14px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' };
const monoBtn = (bg, fg) => ({ cursor: 'pointer', border: '1px solid var(--line)', background: bg, color: fg, fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 18px' });

export default function GiftMembership() {
  const { user, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [plan, setPlan] = useState('monthly');
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [sentGifts, setSentGifts] = useState([]);

  const fetchSent = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/gifts/sent`, { headers: getAuthHeader() });
      setSentGifts(res.data || []);
    } catch { /* silent */ }
  }, [token, getAuthHeader]);

  useEffect(() => { fetchSent(); }, [fetchSent]);

  const startGiftPayment = async () => {
    if (!user) { navigate('/login?redirect=/gift'); return; }
    setError('');
    if (!recipientEmail) { setError('Recipient email required'); return; }
    if (!window.Razorpay) { setError('Razorpay still loading…'); return; }
    setSubmitting(true);
    try {
      const orderRes = await axios.post(`${API}/gifts/create-order`,
        { recipient_email: recipientEmail, recipient_name: recipientName || null, plan, personal_message: message || null, anonymous },
        { headers: getAuthHeader() });
      const rzp = new window.Razorpay({
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'The Hyderabad Library — Gift',
        description: `${plan === 'annual' ? 'Annual' : 'Monthly'} membership for ${recipientName || recipientEmail}`,
        order_id: orderRes.data.order_id,
        prefill: { email: user.email, name: user.name },
        theme: { color: '#31435f' },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/gifts/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers: getAuthHeader() });
            setSuccess({ giftId: verifyRes.data.gift_id });
            await fetchSent();
          } catch (err) {
            setError(err.response?.data?.detail || 'Verification failed. Contact support if money was deducted.');
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start payment');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (s) => s === 'redeemed' ? 'var(--a3)' : s === 'paid' ? 'var(--a1)' : s === 'pending' ? 'var(--a5)' : 'var(--a4)';

  return (
    <PublicLayout theme="archive" grid="--a1" gridStrength={28}>
      <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a2) 0 5px, transparent 6px)' }} />
      <div className="hydlib-pad" style={{ position: 'relative', maxWidth: 760, margin: '0 auto', padding: '64px 48px', boxSizing: 'border-box' }}>
        <h1 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 48, margin: '0 0 8px' }} data-testid="gift-heading">
          <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>🎁 Gift a membership</span>
        </h1>
        <p style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 8px' }}>
          <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>Curated books, hand-picked — the best gift ever</span>
        </p>
        <p style={{ fontSize: 15.5, opacity: 0.85, margin: '0 0 36px', maxWidth: 560 }}>
          They&apos;ll find the gift waiting the next time they sign in with this email.
        </p>

        {!user && (
          <div style={{ border: '1px solid var(--line)', borderLeft: '6px solid var(--a2)', background: 'var(--paper)', padding: '18px 22px', marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15 }}>Please sign in to gift a membership.</span>
            <button onClick={() => navigate('/login?redirect=/gift')} style={monoBtn('var(--ink)', 'var(--paper)')} data-testid="gift-signin-btn">Sign in →</button>
          </div>
        )}

        {success ? (
          <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a3)', background: 'var(--paper)', padding: '36px 34px', textAlign: 'center' }} data-testid="gift-success">
            <span style={{ position: 'absolute', top: -16, right: 18, fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, color: 'var(--a3)', transform: 'rotate(-2deg)', background: 'var(--bg)', padding: '0 8px' }}>you&apos;re a good egg</span>
            <h2 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 32, margin: '0 0 12px' }}>Gift purchased! 🎉</h2>
            <p style={{ fontSize: 15.5, lineHeight: 1.65, margin: '0 0 6px' }}>
              We&apos;ve sent a confirmation to your email. Our team will share the card creative with you so you can pass it on to <strong>{recipientName || recipientEmail}</strong>.
            </p>
            <p style={{ fontSize: 14, opacity: 0.7, margin: '0 0 22px' }}>They&apos;ll see it waiting when they next sign in.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => { setSuccess(null); setRecipientEmail(''); setRecipientName(''); setMessage(''); }} style={monoBtn('var(--a3)', 'var(--o3)')} data-testid="gift-another-btn">Gift another</button>
              <button onClick={() => navigate('/my-library?section=payments')} style={monoBtn('var(--paper)', 'var(--ink)')}>Back to payments</button>
            </div>
          </div>
        ) : (
          user && (
            <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', background: 'var(--paper)', padding: '30px 32px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Plan toggle */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="hydlib-cols">
                {[{ id: 'monthly', label: 'Monthly', price: '₹500', sub: '30 days' }, { id: 'annual', label: 'Annual', price: '₹5,000', sub: '365 days · save ₹1,000' }].map(p => (
                  <button key={p.id} type="button" onClick={() => setPlan(p.id)} data-testid={`gift-plan-${p.id}`}
                    style={{ cursor: 'pointer', textAlign: 'left', border: plan === p.id ? '2px solid var(--a1)' : '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '16px 18px' }}>
                    <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }}>{plan === p.id ? '●' : '○'} {p.label}</div>
                    <div style={{ fontFamily: serifDisplay, fontSize: 26, margin: '4px 0 2px' }}>{p.price}</div>
                    <div style={{ fontSize: 13, opacity: 0.7 }}>{p.sub}</div>
                  </button>
                ))}
              </div>

              <div>
                <label style={labelStyle}>Recipient email</label>
                <input type="email" required value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} style={inputStyle} placeholder="friend@example.com" data-testid="gift-recipient-email" />
              </div>
              <div>
                <label style={labelStyle}>Recipient name (optional)</label>
                <input type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} style={inputStyle} placeholder="Their name (for the card)" data-testid="gift-recipient-name" />
              </div>
              <div>
                <label style={labelStyle}>Personal message (optional)</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Happy birthday — wishing you a year of great books!" data-testid="gift-message" />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="gift-anonymous-toggle" />
                Send anonymously (don&apos;t show my name)
              </label>

              {error && <div style={{ border: '1px dashed var(--a4)', background: 'color-mix(in oklch, var(--a4) 8%, var(--paper))', padding: '10px 14px', fontSize: 14, color: 'var(--a4)' }} data-testid="gift-error">{error}</div>}

              <button
                onClick={startGiftPayment}
                disabled={submitting}
                className="hydlib-highlight"
                data-testid="gift-pay-btn"
                style={{ cursor: submitting ? 'wait' : 'pointer', border: 'none', fontFamily: mono, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: 14, background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`, opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? 'Processing…' : `Pay ₹${plan === 'annual' ? '5,000' : '500'} & send gift`}
              </button>
            </div>
          )
        )}

        {sentGifts.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 14px' }}>Gifts you&apos;ve sent</h3>
            <div style={{ border: '1px solid var(--line)', background: 'var(--paper)', padding: '8px 20px' }}>
              {sentGifts.map(g => (
                <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderBottom: '1px dashed var(--line)', padding: '11px 0', fontSize: 14.5 }} data-testid={`sent-gift-${g.id}`}>
                  <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(g.recipient_name || g.recipient_email)} · {g.plan}
                    <span style={{ opacity: 0.6, fontSize: 12.5 }}> · {new Date(g.created_at).toLocaleDateString('en-IN')}</span>
                  </span>
                  <span style={{ fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: statusColor(g.status) }}>{g.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
